<template>
  <div class="teach min-h-screen">
    <!-- Hero -->
    <section class="px-4 sm:px-6 lg:px-8 pt-14 pb-12 border-b border-gray-800/80 text-center">
      <div class="max-w-3xl mx-auto">
        <p class="text-teal-400 text-xs font-semibold tracking-widest uppercase mb-4">Teach · Learn · Build</p>
        <h1 class="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4 animate-glow">
          Learn AI with Jerome
        </h1>
        <p class="text-lg sm:text-xl text-gray-300 font-light max-w-2xl mx-auto mb-3">
          From your very first prompt to building with agents — guided by JB, Jerome's AI, right inside the chat.
        </p>
        <p class="text-gray-500 text-sm max-w-xl mx-auto">
          No courses to buy. No videos to scrub. You learn by doing, and JB coaches you step by step.
        </p>
        <button type="button" class="teach-cta" @click="startPath">
          <i class="fas fa-chalkboard-user"></i> Start learning in the chat
        </button>
      </div>
    </section>

    <!-- Path -->
    <section class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <h2 class="text-2xl font-bold text-white mb-2 text-center">The path: Beginner → Explorer</h2>
      <p class="text-gray-400 text-sm text-center mb-10 max-w-xl mx-auto">
        Three levels. Each one ends with something real you can do — not a certificate.
      </p>

      <div class="grid gap-6 md:grid-cols-3">
        <article v-for="(level, i) in curriculum" :key="level.id" class="level-card" :style="{ animationDelay: `${i * 90}ms` }">
          <span class="level-badge" :data-level="level.id">{{ level.level }}</span>
          <p class="level-tagline">{{ level.tagline }}</p>
          <ul class="level-topics">
            <li v-for="topic in level.topics" :key="topic">{{ topic }}</li>
          </ul>
          <p class="level-outcome"><strong>Outcome:</strong> {{ level.outcome }}</p>
          <button type="button" class="level-cta" @click="startLevel(level)">
            Start {{ level.level }} <i class="fas fa-arrow-right"></i>
          </button>
        </article>
      </div>
    </section>

    <!-- Lessons -->
    <section class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div class="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 class="text-2xl font-bold text-white">Guided lessons</h2>
          <p class="text-gray-400 text-sm mt-1">Every lesson runs inside the chat — JB walks you through it.</p>
        </div>
      </div>

      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <article v-for="lesson in lessons" :key="lesson.id" class="lesson-tile">
          <div class="lesson-tile-head">
            <span class="tile-level" :data-level="lesson.level">{{ lesson.level }}</span>
            <span class="tile-mins"><i class="far fa-clock"></i> {{ lesson.minutes }} min</span>
          </div>
          <h3 class="tile-title">{{ lesson.title }}</h3>
          <ol class="tile-steps">
            <li v-for="step in lesson.steps.slice(0, 2)" :key="step">{{ step }}</li>
            <li v-if="lesson.steps.length > 2" class="tile-more">+{{ lesson.steps.length - 2 }} more steps</li>
          </ol>
          <button type="button" class="tile-cta" @click="startLesson(lesson)">
            <i class="fas fa-chalkboard-user"></i> Launch in chat
          </button>
        </article>
      </div>
    </section>

    <!-- CTA -->
    <section class="max-w-3xl mx-auto px-4 sm:px-6 pb-20 text-center">
      <div class="rounded-2xl border border-teal-500/30 bg-teal-500/5 px-6 py-10">
        <h2 class="text-2xl font-bold text-white mb-3">The best way to learn AI is to use it — today.</h2>
        <p class="text-gray-400 text-sm max-w-md mx-auto mb-6">
          Open the chat, tell JB your level, and he'll coach you through your first lesson right now.
        </p>
        <button type="button" class="teach-cta" @click="startPath">
          <i class="fas fa-wand-magic-sparkles"></i> Teach me, JB
        </button>
      </div>
    </section>
  </div>
</template>

<script setup>
import kb from '../../shared/knowledge.cjs';
import { openDock } from '../stores/chatDock.js';
import { trackEvent } from '../utils/trackEvent.js';

const curriculum = kb.TEACH_CURRICULUM;
const lessons = kb.LESSONS;

function startPath() {
  trackEvent('jb_teach_start', { via: 'teach-page' });
  openDock("Teach me AI — start me at the beginner level.");
}

function startLevel(level) {
  trackEvent('jb_teach_level', { level: level.id, via: 'teach-page' });
  openDock(`Teach me AI — start me at the ${level.level} level.`);
}

function startLesson(lesson) {
  trackEvent('jb_teach_lesson', { lesson: lesson.id, via: 'teach-page' });
  openDock(`Start the "${lesson.title}" lesson.`);
}
</script>

<style scoped>
@keyframes glow {
  0%, 100% { text-shadow: 0 0 20px rgba(45, 212, 191, 0.35); }
  50% { text-shadow: 0 0 40px rgba(45, 212, 191, 0.6), 0 0 60px rgba(59, 130, 246, 0.25); }
}

.animate-glow { animation: glow 3s infinite; }

@keyframes pop-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.teach-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.8rem 1.6rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  background: #2dd4bf;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.teach-cta:hover {
  background: #5eead4;
  transform: translateY(-2px);
  box-shadow: 0 12px 28px -8px rgba(45, 212, 191, 0.5);
}

/* Level cards */
.level-card {
  display: flex;
  flex-direction: column;
  padding: 1.4rem 1.3rem;
  border-radius: 1rem;
  border: 1px solid rgba(75, 85, 99, 0.5);
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(8px);
  animation: pop-in 0.5s ease both;
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

.level-card:hover {
  border-color: rgba(45, 212, 191, 0.45);
  transform: translateY(-4px);
  box-shadow: 0 18px 36px -14px rgba(45, 212, 191, 0.3);
}

.level-badge {
  align-self: flex-start;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding: 0.28rem 0.65rem;
  border-radius: 9999px;
  color: #5eead4;
  background: rgba(45, 212, 191, 0.1);
  border: 1px solid rgba(45, 212, 191, 0.4);
  margin-bottom: 0.8rem;
}

.level-badge[data-level='practitioner'] { color: #93c5fd; background: rgba(59, 130, 246, 0.1); border-color: rgba(59, 130, 246, 0.45); }
.level-badge[data-level='explorer'] { color: #f0abfc; background: rgba(217, 70, 239, 0.1); border-color: rgba(217, 70, 239, 0.45); }

.level-tagline {
  margin: 0 0 0.9rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
}

.level-topics {
  margin: 0 0 1rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;
}

.level-topics li {
  position: relative;
  padding-left: 1rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: #9ca3af;
}

.level-topics li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #2dd4bf;
}

.level-outcome {
  margin: 0 0 1.1rem;
  font-size: 0.78rem;
  line-height: 1.55;
  color: #d1d5db;
}

.level-outcome strong { color: #5eead4; }

.level-cta {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
  background: #2dd4bf;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.level-cta:hover { background: #5eead4; }

/* Lesson tiles */
.lesson-tile {
  display: flex;
  flex-direction: column;
  padding: 1.2rem 1.2rem 1.3rem;
  border-radius: 1rem;
  border: 1px solid rgba(75, 85, 99, 0.5);
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(8px);
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

.lesson-tile:hover {
  border-color: rgba(45, 212, 191, 0.45);
  transform: translateY(-4px);
  box-shadow: 0 16px 32px -14px rgba(45, 212, 191, 0.3);
}

.lesson-tile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7rem;
}

.tile-level {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.22rem 0.55rem;
  border-radius: 9999px;
  color: #5eead4;
  background: rgba(45, 212, 191, 0.1);
  border: 1px solid rgba(45, 212, 191, 0.4);
}

.tile-level[data-level='practitioner'] { color: #93c5fd; background: rgba(59, 130, 246, 0.1); border-color: rgba(59, 130, 246, 0.45); }
.tile-level[data-level='explorer'] { color: #f0abfc; background: rgba(217, 70, 239, 0.1); border-color: rgba(217, 70, 239, 0.45); }

.tile-mins {
  font-size: 0.68rem;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.tile-title {
  margin: 0 0 0.7rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
}

.tile-steps {
  margin: 0 0 1.1rem;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
}

.tile-steps li {
  font-size: 0.78rem;
  line-height: 1.5;
  color: #9ca3af;
}

.tile-more { color: #5eead4; font-weight: 600; font-size: 0.72rem; }

.tile-cta {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #5eead4;
  background: transparent;
  border: 1px solid rgba(45, 212, 191, 0.5);
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.tile-cta:hover {
  background: rgba(45, 212, 191, 0.1);
  border-color: rgba(45, 212, 191, 0.9);
}

@media (prefers-reduced-motion: reduce) {
  .animate-glow,
  .level-card {
    animation: none;
  }
}
</style>
